import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Snackbar,
  Tooltip,
  InputAdornment,
  Fab,
  LinearProgress
} from '@mui/material';
import {
  Business as BusinessIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface Department {
  _id: string;
  name: string;
  description: string;
  manager?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  isActive: boolean;
  settings: {
    maxEmployees?: number;
    allowSelfRegistration: boolean;
  };
  stats: {
    totalEmployees: number;
    activeEmployees: number;
    totalProgress: number;
    utilizationRate?: number;
  };
  createdAt: string;
  updatedAt: string;
}

interface DepartmentFormData {
  name: string;
  description: string;
  manager: string;
  maxEmployees: string;
  allowSelfRegistration: boolean;
}

const CompanyDepartments: React.FC = () => {
  const { user } = useAuth();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Estados para el diálogo de formulario
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
  const [formData, setFormData] = useState<DepartmentFormData>({
    name: '',
    description: '',
    manager: '',
    maxEmployees: '',
    allowSelfRegistration: true
  });
  const [formLoading, setFormLoading] = useState(false);
  
  // Estados para el diálogo de detalles
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [departmentEmployees, setDepartmentEmployees] = useState<any[]>([]);
  
  // Estados para notificaciones
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info'
  });

  // Lista de empleados para seleccionar como manager
  const [employees, setEmployees] = useState<any[]>([]);

  useEffect(() => {
    loadDepartments();
    loadEmployees();
  }, []);

  const loadDepartments = async () => {
    try {
      setLoading(true);
      const response = await api.getDepartments();
      setDepartments(response);
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error cargando departamentos',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await api.getCompanyEmployees();
      setEmployees(response.employees || []);
    } catch (err: any) {
      console.error('Error cargando empleados:', err);
    }
  };

  const handleOpenFormDialog = (department?: Department) => {
    if (department) {
      setEditingDepartment(department);
      setFormData({
        name: department.name,
        description: department.description || '',
        manager: department.manager?._id || '',
        maxEmployees: department.settings.maxEmployees?.toString() || '',
        allowSelfRegistration: department.settings.allowSelfRegistration
      });
    } else {
      setEditingDepartment(null);
      setFormData({
        name: '',
        description: '',
        manager: '',
        maxEmployees: '',
        allowSelfRegistration: true
      });
    }
    setFormDialogOpen(true);
  };

  const handleCloseFormDialog = () => {
    setFormDialogOpen(false);
    setEditingDepartment(null);
    setFormData({
      name: '',
      description: '',
      manager: '',
      maxEmployees: '',
      allowSelfRegistration: true
    });
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const departmentData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        manager: formData.manager || null,
        settings: {
          maxEmployees: formData.maxEmployees ? parseInt(formData.maxEmployees) : null,
          allowSelfRegistration: formData.allowSelfRegistration
        }
      };

      if (editingDepartment) {
        await api.updateDepartment(editingDepartment._id, departmentData);
        setSnackbar({
          open: true,
          message: 'Departamento actualizado exitosamente',
          severity: 'success'
        });
      } else {
        await api.createDepartment(departmentData);
        setSnackbar({
          open: true,
          message: 'Departamento creado exitosamente',
          severity: 'success'
        });
      }

      handleCloseFormDialog();
      loadDepartments();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error guardando departamento',
        severity: 'error'
      });
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteDepartment = async (department: Department) => {
    if (!window.confirm(`¿Está seguro de que desea eliminar el departamento "${department.name}"?`)) {
      return;
    }

    try {
      await api.deleteDepartment(department._id);
      setSnackbar({
        open: true,
        message: 'Departamento eliminado exitosamente',
        severity: 'success'
      });
      loadDepartments();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error eliminando departamento',
        severity: 'error'
      });
    }
  };

  const handleViewDepartmentDetails = async (department: Department) => {
    try {
      console.log('🔍 Abriendo detalles del departamento:', department.name, department._id);
      setDetailLoading(true);
      setSelectedDepartment(department);
      
      console.log('📡 Llamando a getDepartmentEmployees con ID:', department._id);
      const response = await api.getDepartmentEmployees(department._id);
      console.log('📡 Respuesta recibida:', response);
      
      // El backend retorna { data: { department, employees } }
      const employees = response.data?.employees || response.employees || [];
      console.log('👥 Empleados encontrados:', employees.length);
      setDepartmentEmployees(employees);
      setDetailDialogOpen(true);
      console.log('✅ Diálogo de detalles abierto exitosamente');
    } catch (err: any) {
      console.error('❌ Error en handleViewDepartmentDetails:', err);
      console.error('❌ Error response:', err.response);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error cargando detalles del departamento',
        severity: 'error'
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const getStatusLabel = (isActive: boolean) => isActive ? 'Activo' : 'Inactivo';
  const getStatusColor = (isActive: boolean) => isActive ? 'success' : 'error';

  const filteredDepartments = departments.filter(dept => {
    const matchesSearch = dept.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         dept.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && dept.isActive) ||
                         (statusFilter === 'inactive' && !dept.isActive);
    
    return matchesSearch && matchesStatus;
  });

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          <BusinessIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          Gestión de Departamentos
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Administra los departamentos de tu empresa
        </Typography>
      </Box>

      {/* Filtros y búsqueda */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 2,
            alignItems: 'end'
          }}>
            <TextField
              label="Buscar departamentos"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              fullWidth
            />
            
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Estado"
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="active">Activos</MenuItem>
                <MenuItem value="inactive">Inactivos</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Typography variant="body2" color="text.secondary">
                Total: {filteredDepartments.length} departamentos
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Tarjetas de departamentos */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
        gap: 3 
      }}>
        {loading ? (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <LinearProgress />
          </Box>
        ) : filteredDepartments.length === 0 ? (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  No se encontraron departamentos
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {searchTerm || statusFilter !== 'all' 
                    ? 'Intenta ajustar los filtros de búsqueda'
                    : 'Crea tu primer departamento para comenzar'
                  }
                </Typography>
              </CardContent>
            </Card>
          </Box>
        ) : (
          filteredDepartments.map((department) => (
            <Box key={department._id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" component="h3">
                      {department.name}
                    </Typography>
                    <Chip
                      label={getStatusLabel(department.isActive)}
                      color={getStatusColor(department.isActive)}
                      size="small"
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {department.description || 'Sin descripción'}
                  </Typography>

                  {department.manager && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Gerente:
                      </Typography>
                      <Typography variant="body2">
                        {department.manager.firstName} {department.manager.lastName}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PeopleIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {department.stats.totalEmployees} empleados
                      </Typography>
                    </Box>
                    
                    {department.settings.maxEmployees && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUpIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {Math.round(department.stats.utilizationRate || 0)}%
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {department.settings.maxEmployees && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Capacidad
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {department.stats.totalEmployees} / {department.settings.maxEmployees}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={department.stats.utilizationRate || 0}
                        color={department.stats.utilizationRate && department.stats.utilizationRate > 90 ? 'error' : 'primary'}
                      />
                    </Box>
                  )}
                </CardContent>
                
                <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Tooltip title="Ver detalles">
                    <IconButton 
                      size="small" 
                      color="primary"
                      onClick={() => handleViewDepartmentDetails(department)}
                    >
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Editar">
                    <IconButton 
                      size="small" 
                      color="primary"
                      onClick={() => handleOpenFormDialog(department)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar">
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => handleDeleteDepartment(department)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Card>
            </Box>
          ))
        )}
      </Box>

      {/* Botón flotante para agregar */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => handleOpenFormDialog()}
      >
        <AddIcon />
      </Fab>

      {/* Diálogo de formulario */}
      <Dialog
        open={formDialogOpen}
        onClose={handleCloseFormDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingDepartment ? 'Editar Departamento' : 'Nuevo Departamento'}
        </DialogTitle>
        <form onSubmit={handleFormSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label="Nombre del departamento"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                fullWidth
              />
              
              <TextField
                label="Descripción"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
                fullWidth
              />

              <FormControl fullWidth>
                <InputLabel>Gerente del departamento</InputLabel>
                <Select
                  value={formData.manager}
                  onChange={(e) => setFormData({ ...formData, manager: e.target.value })}
                  label="Gerente del departamento"
                >
                  <MenuItem value="">Sin gerente asignado</MenuItem>
                  {employees.map((employee) => (
                    <MenuItem key={employee._id} value={employee._id}>
                      {employee.firstName} {employee.lastName} ({employee.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Máximo de empleados (opcional)"
                type="number"
                value={formData.maxEmployees}
                onChange={(e) => setFormData({ ...formData, maxEmployees: e.target.value })}
                inputProps={{ min: 1 }}
                fullWidth
              />

              <FormControl fullWidth>
                <InputLabel>Permitir auto-registro</InputLabel>
                <Select
                  value={formData.allowSelfRegistration ? 'true' : 'false'}
                  onChange={(e) => setFormData({ ...formData, allowSelfRegistration: e.target.value === 'true' })}
                  label="Permitir auto-registro"
                >
                  <MenuItem value="true">Sí</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseFormDialog} disabled={formLoading}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" disabled={formLoading}>
              {formLoading ? 'Guardando...' : (editingDepartment ? 'Actualizar' : 'Crear')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Diálogo de detalles del departamento */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => {
          setDetailDialogOpen(false);
          setSelectedDepartment(null);
          setDepartmentEmployees([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <BusinessIcon />
            <Typography variant="h6">
              {selectedDepartment?.name}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <Typography>Cargando detalles...</Typography>
            </Box>
          ) : selectedDepartment ? (
            <Box>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {selectedDepartment.description || 'Sin descripción'}
              </Typography>

              <Typography variant="h6" gutterBottom>
                Empleados del Departamento
              </Typography>
              
              {departmentEmployees.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No hay empleados asignados a este departamento
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Empleado</TableCell>
                        <TableCell>Cargo</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell>Último Acceso</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {departmentEmployees.map((employee) => (
                        <TableRow key={employee._id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                                {employee.firstName.charAt(0).toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2">
                                  {employee.firstName} {employee.lastName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {employee.email}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {employee.position || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getStatusLabel(employee.isActive)}
                              color={getStatusColor(employee.isActive)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {employee.lastLogin 
                                ? new Date(employee.lastLogin).toLocaleDateString('es-ES')
                                : 'Nunca'
                              }
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDetailDialogOpen(false);
            setSelectedDepartment(null);
            setDepartmentEmployees([]);
          }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CompanyDepartments;
